<template>
  <CommonLayout selectedNav="ocr">
    <div class="tx-wrap">
      <div class="tx-hero tx-hero-left">
        <h1 class="tx-hero-title">OCR <em>识别</em></h1>
        <p class="tx-hero-sub">扫描件 · 图片 → Word / 文本（支持表格 · 公式还原，长文档后台解析）</p>
        <p class="tx-privacy"><i class="el-icon-lock"></i> 隐私保护：上传文件与识别结果将在 7 天后自动删除</p>
      </div>

      <div class="tx-panel tx-ocr-panel">
        <span class="tx-corner tl"></span><span class="tx-corner tr"></span>
        <span class="tx-corner bl"></span><span class="tx-corner br"></span>

        <!-- 上传区 -->
        <el-upload class="tx-ocr-upload" drag accept=".png,.jpg,.jpeg,.bmp,.webp,.tif,.tiff,.pdf"
          :auto-upload="false" :show-file-list="false" :on-change="onFile">
          <i class="el-icon-upload tx-upload-icon"></i>
          <div class="el-upload__text tx-upload-text">
            拖入 <em>扫描件 PDF</em> 或 <em>图片</em>，或 <el-button type="text" class="tx-upload-btn">点击选择</el-button>
          </div>
          <div class="el-upload__tip tx-upload-tip">支持 PNG / JPG / BMP / WEBP / TIFF / PDF · 单文件不超过 {{ maxUploadSizeText }} · 解析在后台进行</div>
        </el-upload>

        <div class="tx-ocr-file" v-if="picked">
          <i class="el-icon-document"></i>
          <span class="tx-ocr-file-name">{{ picked.name }}</span>
          <span class="tx-ocr-file-size" v-if="picked.size">{{ (picked.size / 1024 / 1024).toFixed(2) }} MB</span>
          <el-tag v-if="reusedFile" size="mini" class="tx-ocr-reused">已复用</el-tag>
          <el-link type="info" :underline="false" @click="clearFile"><i class="el-icon-close"></i></el-link>
        </div>
        <div class="tx-ocr-reuse-tip" v-if="reusedFile">
          该文件已在「文档转换」上传完成，无需重新选择 —— 确认输出格式后点「开始识别」即可。
        </div>

        <!-- 网页版免登录、免费：不再展示配额与「开通套餐」入口 -->

        <!-- 输出格式 + 提交 -->
        <div class="tx-ocr-bar">
          <div class="tx-field-label">// 输出格式 TARGET</div>
          <el-select v-model="target" class="tx-ocr-select" size="small">
            <el-option v-for="t in targetOptions" :key="t.v" :label="t.t" :value="t.v"></el-option>
          </el-select>
          <el-button type="primary" class="tx-ocr-go" size="small" :loading="submitting"
            :disabled="!picked" @click="submit">
            {{ submitting ? '提交中…' : '开始识别' }}
          </el-button>
        </div>

        <!-- 错误提示 -->
        <div class="tx-result-err" v-if="error">
          <i class="el-icon-warning-outline"></i> {{ error }}
          <div class="tx-err-hint" v-if="hint">{{ hint }}</div>
        </div>

        <!-- 解析任务列表 -->
        <div class="tx-ocr-tasks" v-if="tasks.length">
          <div class="tx-io-label">
            <span>解析任务 TASKS</span>
            <el-link type="info" :underline="false" @click="clearDone" v-if="hasFinished">清空已完成</el-link>
          </div>
          <el-table :data="tasks" class="tx-task-table" size="mini">
            <el-table-column prop="fileName" label="文件" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column label="格式" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.target.toUpperCase() }}</template>
            </el-table-column>
            <el-table-column label="状态" width="170">
              <template slot-scope="scope">
                <span class="tx-status" :class="'st-' + scope.row.status">{{ statusText(scope.row.status) }}</span>
                <span class="tx-status-pct" v-if="scope.row.status === 'running'">{{ scope.row.progress }}%</span>
                <span class="tx-status-pct"
                  v-else-if="scope.row.status === 'queued' && typeof scope.row.queuePosition === 'number'">
                  第 {{ scope.row.queuePosition + 1 }} 位
                </span>
                <!-- 预估剩余时间：进度动起来后才显示（估不出就不显示，不给错的时间承诺） -->
                <div v-if="scope.row.status === 'running' && etaOf(scope.row)" class="tx-eta">
                  {{ etaOf(scope.row) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column label="进度" min-width="120">
              <template slot-scope="scope">
                <el-progress :percentage="scope.row.progress" :stroke-width="6" :show-text="false"
                  :status="scope.row.status === 'failed' ? 'exception' : (scope.row.status === 'success' ? 'success' : null)"></el-progress>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" align="center">
              <template slot-scope="scope">
                <el-link type="success" :underline="false" v-if="scope.row.status === 'success'"
                  @click="download(scope.row)"><i class="el-icon-download"></i> 下载</el-link>
                <span v-else-if="scope.row.status === 'failed'" class="tx-fail-tip" :title="scope.row.error">
                  <i class="el-icon-warning-outline"></i> 失败
                </span>
                <span v-else class="tx-wait-tip">—</span>
              </template>
            </el-table-column>
          </el-table>
          <div class="tx-task-err" v-if="failedMsg">{{ failedMsg }}</div>
        </div>
      </div>
    </div>
  </CommonLayout>
</template>

<script>
import CommonLayout from './common/commonPage.vue';
import { createOcrTask, getTask, fetchTaskResult } from '../api/conversion';
import { triggerDownload, normalizeError } from '../api/request';
import { addTask, RETENTION_HOURS, takeOcrHandoff } from '../store';
import {
  progressLine,
  ensureNotifyPermission,
  notifyTaskDone,
  titleHint
} from '../utils/task';
import {
  MAX_UPLOAD_SIZE_TEXT,
  checkFileSize,
} from '../utils/upload-limit';

export default {
  components: { CommonLayout },
  props: { selectedNav: { type: String, default: 'ocr' } },
  data() {
    return {
      file: null,
      // 从「文档转换」页带过来的已上传文件：{ name, size, fileId }
      // 有它就不需要用户再选一次，提交时直接复用后端文件（fileId）
      reusedFile: null,
      target: 'docx',
      targetOptions: [
        { v: 'docx', t: 'Word 文档 (.docx)' },
        { v: 'txt', t: '纯文本 (.txt)' },
        { v: 'html', t: '网页 (.html)' },
        { v: 'md', t: 'Markdown (.md)' },
        { v: 'json', t: '结构化数据 (.json)' }
      ],
      submitting: false,
      tasks: [],
      timers: {},
      error: '',
      hint: '',
      // 上传限制来自 SSOT（src/utils/upload-limit.js）
      maxUploadSizeText: MAX_UPLOAD_SIZE_TEXT
    };
  },
  computed: {
    /** 当前待识别文件：新选的本地文件优先，其次是转换页交接下来的已上传文件 */
    picked() {
      return this.file || this.reusedFile;
    },
    hasFinished() {
      return this.tasks.some((t) => t.status === 'success' || t.status === 'failed');
    },
    failedMsg() {
      const f = this.tasks.find((t) => t.status === 'failed' && t.error);
      return f ? `「${f.fileName}」${f.error}` : '';
    },
  },
  methods: {
    onFile(f) {
      if (!f || !f.raw) return;
      // 单文件大小校验：选中即刻拦截，避免白等整个文件传完才被后端拒
      const sizeErr = checkFileSize({ name: f.name, size: f.raw.size });
      if (sizeErr) {
        this.$message.error(sizeErr);
        return;
      }
      // 手动重新选择 = 放弃转换页带过来的那份，之后按新文件正常上传
      this.reusedFile = null;
      this.file = f.raw;
      this.error = '';
      this.hint = '';
    },
    clearFile() {
      this.file = null;
      this.reusedFile = null;
    },
    submit() {
      if (!this.picked || this.submitting) return;
      this.submitting = true;
      this.error = '';
      this.hint = '';
      const picked = this.picked;
      const fmt = this.target;
      // 桌面通知授权：必须在用户「点击提交」的上下文里请求，浏览器才肯弹授权框。
      // 用户拒绝也不影响任务 —— 页面内提示与任务中心照样能看到结果。
      ensureNotifyPermission();
      const payload = picked.fileId
        // 转换页已经传过这份文件：只传 fileId，不再重复上传
        ? { fileId: picked.fileId, target: fmt, mode: 'auto', engine: 'auto' }
        : { file: picked, target: fmt, mode: 'auto', engine: 'auto' };
      createOcrTask(payload)
        .then((resp) => {
          const data = resp.data || {};
          if (!data.taskId) throw new Error('任务创建失败');
          const task = {
            id: data.taskId,
            fileName: picked.name,
            source: (picked.name.split('.').pop() || 'file').toLowerCase(),
            target: fmt,
            kind: 'ocr',
            mode: 'auto',
            status: data.status || 'queued',
            queuePosition: typeof data.queuePosition === 'number' ? data.queuePosition : null,
            progress: 0,
            // 页进度 / 剩余时间：全部由后端下发（轮询时覆盖）。
            // ⛔ 前端不再自己在本地起一个"开始时间"去推算 —— 那套算法会越算越长。
            pagesDone: 0,
            pagesTotal: 0,
            etaSeconds: null,
            result: '',
            error: '',
            createdAt: Date.now(),
            retentionHours: RETENTION_HOURS
          };
          this.tasks.unshift(task);
          // 同一份对象引用放进任务中心 store：轮询更新状态后，任务中心实时同步
          addTask(task);
          this.clearFile();
          this.$message.success(`「${task.fileName}」已提交解析`);
          this.pollTask(task);
        })
        .catch((e) => {
          // 网页版免登录、免费：直接展示后端错误提示，不再引导登录 / 订阅
          const info = normalizeError(e);
          this.error = info.message;
          this.hint = info.hint;
        })
        .finally(() => { this.submitting = false; });
    },
    /** 运行中任务的页进度 + 剩余时间文案（只读后端字段；估不出返回空串，不显示） */
    etaOf(row) {
      return progressLine(row);
    },
    /**
     * 任务到达终态：页面标题提示 + 桌面通知（仅当页面在后台时才弹，
     * 人就在页面上时不打扰）。标题 8 秒后恢复，避免一直挂着「已完成」。
     */
    onTaskDone(task, ok) {
      titleHint(ok ? '✓ 已完成' : '✕ 失败');
      notifyTaskDone(
        ok ? '解析完成' : '解析失败',
        `「${task.fileName}」${ok ? '已处理完成，可下载' : '处理失败，请查看任务中心'}`
      );
      clearTimeout(this._titleTimer);
      this._titleTimer = setTimeout(() => {
        // 还有任务在跑就保持「处理中」，否则恢复原标题
        const running = this.tasks.some((t) => t.status !== 'success' && t.status !== 'failed');
        titleHint(running ? '处理中' : '');
      }, 8000);
    },
    pollTask(task) {
      if (!task.id) return;
      getTask(task.id)
        .then((resp) => {
          const d = resp.data || {};
          task.status = d.status;
          task.progress = typeof d.progress === 'number' ? d.progress : task.progress;
          if (d.result) task.result = d.result;
          if (d.error) task.error = typeof d.error === 'string' ? d.error : JSON.stringify(d.error);
          if (typeof d.queuePosition === 'number') task.queuePosition = d.queuePosition;
          // 页进度与实测剩余时间（后端下发；缺字段时保持上一次的值，别显示成 0/0）
          if (typeof d.pagesDone === 'number') task.pagesDone = d.pagesDone;
          if (typeof d.pagesTotal === 'number') task.pagesTotal = d.pagesTotal;
          if (d.etaSeconds !== undefined) {
            task.etaSeconds = (d.etaSeconds === null ? null : Number(d.etaSeconds));
          }
          if (d.status === 'success') {
            task.progress = 100;
            this.$message.success(`「${task.fileName}」解析完成，可下载`);
            this.onTaskDone(task, true);
            return;
          }
          if (d.status === 'failed') {
            this.$message.error(`「${task.fileName}」解析失败`);
            this.onTaskDone(task, false);
            return;
          }
          this.schedulePoll(task);
        })
        .catch(() => {
          if (task.status !== 'success' && task.status !== 'failed') this.schedulePoll(task);
        });
    },
    schedulePoll(task) {
      clearTimeout(this.timers[task.id]);
      this.timers[task.id] = setTimeout(() => this.pollTask(task), 1500);
    },
    statusText(status) {
      return { queued: '排队中', running: '解析中', success: '完成', failed: '失败' }[status] || status;
    },
    download(row) {
      const base = (row.fileName || 'scan').replace(/\.[^.]+$/, '');
      this.$message.info('开始下载，大文件可能需要一些时间…');
      fetchTaskResult(row.id)
        .then((resp) => triggerDownload(resp.data, `${base}_ocr.${row.target}`))
        .then(() => this.$message.success('下载完成'))
        .catch(() => this.$message.error('下载失败，请稍后重试'));
    },
    clearDone() {
      this.tasks = this.tasks.filter((t) => t.status !== 'success' && t.status !== 'failed');
    }
  },
  created() {
    // 从「文档转换」页跳过来时，文件已经上传过了：取回存储名直接用
    const handoff = takeOcrHandoff();
    if (handoff && handoff.fileId) {
      this.reusedFile = {
        name: handoff.name || handoff.fileId,
        size: handoff.size || 0,
        fileId: handoff.fileId
      };
      // 用户在转换页选的目标格式如果 OCR 也支持，直接沿用（如原本要 txt）
      const t = String(handoff.target || '').toLowerCase();
      if (this.targetOptions.some((o) => o.v === t)) this.target = t;
    }
  },
  beforeDestroy() {
    Object.values(this.timers).forEach(clearTimeout);
    clearTimeout(this._titleTimer);
    titleHint('');                       // 离开页面别把标题留在「已完成」
  }
};
</script>

<style scoped>
.tx-ocr-panel {
  position: relative;
  padding: 28px;
}

.tx-upload-icon {
  font-size: 52px;
  color: #22d3ee !important;
}

.tx-upload-text {
  color: #c7d7ee !important;
}

.tx-upload-text em {
  color: #22d3ee;
  font-style: normal;
}

.tx-upload-btn {
  color: #22d3ee !important;
}

.tx-upload-tip {
  color: #5b6b8c !important;
}

.tx-ocr-file {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 14px;
  border: 1px solid rgba(56, 189, 248, .3);
  background: rgba(7, 12, 24, .6);
  border-radius: 8px;
  color: #c7d7ee;
}

.tx-ocr-file-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tx-ocr-file-size {
  color: #5b6b8c;
  font-size: 12px;
}

.tx-ocr-reused {
  background: rgba(52, 211, 153, .12) !important;
  border-color: rgba(52, 211, 153, .4) !important;
  color: #34d399 !important;
}

.tx-ocr-reuse-tip {
  margin-top: 8px;
  color: #5b7295;
  font-size: 12px;
  line-height: 1.6;
}

.tx-ocr-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
}

.tx-ocr-quota {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 8px 12px;
  border: 1px solid rgba(56, 189, 248, .28);
  background: rgba(7, 12, 24, .5);
  border-radius: 8px;
  color: #c7d7ee;
  font-size: 13px;
}

.tx-ocr-quota .el-icon-info {
  color: #22d3ee;
}

.tx-ocr-upgrade {
  margin-left: 4px;
}

.tx-field-label {
  color: #5b6b8c;
  font-size: 12px;
  letter-spacing: 1px;
}

.tx-ocr-select {
  width: 220px;
}

.tx-ocr-go {
  background: linear-gradient(90deg, #22d3ee, #818cf8) !important;
  border: none !important;
  color: #04101e !important;
  font-weight: 600;
}

.tx-ocr-tasks {
  margin-top: 26px;
}

.tx-io-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #8fa3c8;
  margin-bottom: 10px;
}

.tx-task-table {
  background: transparent !important;
}

.tx-status {
  font-size: 12px;
}

.st-running {
  color: #22d3ee;
}

.st-success {
  color: #34d399;
}

.st-failed {
  color: #fca5a5;
}

.st-queued {
  color: #8fa3c8;
}

.tx-status-pct {
  margin-left: 8px;
  color: #5b6b8c;
  font-size: 12px;
}

/* 预估剩余时间：进度动起来后才出现，估不出就整行不渲染 */
.tx-eta {
  margin-top: 4px;
  color: #8fd8ee;
  font-size: 12px;
}

.tx-wait-tip {
  color: #5b6b8c;
}

.tx-fail-tip {
  color: #fca5a5;
  cursor: help;
}

.tx-task-err {
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(248, 113, 113, .4);
  background: rgba(127, 29, 29, .15);
  border-radius: 8px;
  color: #fca5a5;
  font-size: 12px;
}

.tx-result-err {
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px solid rgba(248, 113, 113, .4);
  background: rgba(127, 29, 29, .15);
  border-radius: 8px;
  color: #fca5a5;
}

.tx-err-hint {
  margin-top: 6px;
  color: #5b6b8c;
  font-size: 12px;
}

.tx-privacy {
    margin: 6px 0 0;
    color: #5b7295;
    font-size: 12px;
    letter-spacing: .5px;
}
.tx-privacy i { color: #34d399; margin-right: 4px; }

</style>
